import { Component } from '@theme/component';

/**
 * Viewport-gated MP4 playback for the 50/50 section overlay (ODB-30).
 * Videos live in `.section-50-50-animation__video-overlay` (sibling of images), not inside the image flex/grid cell.
 *
 * @extends {Component<{}>}
 */
class FiftyFiftyAnimation extends Component {
  /** @type {IntersectionObserver | undefined} */
  #intersectionObserver;

  /** @type {MediaQueryList} */
  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /** @type {MediaQueryList} */
  #desktop = window.matchMedia('(min-width: 990px)');

  /** @type {(() => void) | undefined} */
  #resizeHandler;

  /** @type {boolean} */
  #inView = false;

  connectedCallback() {
    super.connectedCallback();

    if (this.dataset.enableAnimation !== 'true') return;

    const videoRoot = this.querySelector('.section-50-50-animation__video-overlay');
    if (!(videoRoot instanceof HTMLElement)) return;

    const videos = videoRoot.querySelectorAll('video');
    if (videos.length === 0) return;

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#inView = true;
            this.#syncVideoPlayback(videoRoot);
          } else {
            this.#inView = false;
            this.#pauseAllVideos(videoRoot);
          }
        }
      },
      { threshold: 0.2, rootMargin: '0px 0px -10% 0px' }
    );

    this.#intersectionObserver.observe(this);

    this.#resizeHandler = () => {
      if (!this.#inView) return;
      this.#syncVideoPlayback(videoRoot);
    };

    window.addEventListener('resize', this.#resizeHandler);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = undefined;

    if (this.#resizeHandler) {
      window.removeEventListener('resize', this.#resizeHandler);
      this.#resizeHandler = undefined;
    }
  }

  /**
   * @param {HTMLElement} videoRoot
   */
  #pauseAllVideos(videoRoot) {
    for (const video of videoRoot.querySelectorAll('video')) {
      if (!(video instanceof HTMLVideoElement)) continue;
      video.pause();
      video.currentTime = 0;
    }
  }

  /**
   * @param {HTMLElement} videoRoot
   */
  #syncVideoPlayback(videoRoot) {
    if (this.#reducedMotion.matches) {
      this.#pauseAllVideos(videoRoot);
      return;
    }

    const single = videoRoot.querySelector('.section-50-50-animation__video-slot--all video');

    if (single instanceof HTMLVideoElement) {
      for (const video of videoRoot.querySelectorAll('video')) {
        if (video !== single) {
          video.pause();
          video.currentTime = 0;
        }
      }
      single.currentTime = 0;
      single.play().catch(() => {});
      return;
    }

    const desktopVideo = videoRoot.querySelector('.section-50-50-animation__video-slot--desktop video');
    const mobileVideo = videoRoot.querySelector('.section-50-50-animation__video-slot--mobile video');

    /** @type {HTMLVideoElement | null} */
    let active = null;

    if (this.#desktop.matches && desktopVideo instanceof HTMLVideoElement) {
      active = desktopVideo;
    } else if (mobileVideo instanceof HTMLVideoElement) {
      active = mobileVideo;
    } else if (desktopVideo instanceof HTMLVideoElement) {
      active = desktopVideo;
    }

    for (const video of videoRoot.querySelectorAll('video')) {
      if (video !== active) {
        video.pause();
        video.currentTime = 0;
      }
    }

    if (active instanceof HTMLVideoElement) {
      active.currentTime = 0;
      active.play().catch(() => {});
    }
  }
}

if (!customElements.get('fifty-fifty-animation')) {
  customElements.define('fifty-fifty-animation', FiftyFiftyAnimation);
}
